import { ADMIN_PAYOUT_STATUSES } from '@carcheck/shared';
import { z } from 'zod';

import type { StatusOption } from '../../../components/filters/StatusMultiSelect';

/**
 * URL filter schema for the payouts queue (AUT-128). Mirrors the backend
 * `ListAdminPayoutsQuerySchema` — `status` is a multi-select over the
 * THREE wire-stable `ADMIN_PAYOUT_STATUSES` (`pending` / `completed` /
 * `failed`), `inspectorId` is an exact-match UUID (the admin drills into
 * one inspector's payouts), `dateFrom` / `dateTo` are ISO date strings
 * binding the backend's `createdAt` filter.
 *
 * There is NO default `status` — the queue opens unfiltered, showing
 * every payout. A default chip is not removable here: clearing it drops
 * the param from the URL, which is indistinguishable from "never set",
 * so the default would immediately reapply. The dead variants
 * `scheduled` / `processing` / `superseded` are NOT offered (never on
 * the wire). There is NO `q` free-text filter and NO `rail` filter —
 * neither is in the backend query schema.
 *
 * Every key has a `.catch` default so a malformed / absent query param
 * degrades safely instead of throwing — `useUrlFilters` parses the raw
 * query string straight through.
 */

/**
 * `status=failed,pending` → string[] of valid statuses. Absent or empty
 * param → `[]` (no status filter — the full queue).
 */
const statusSchema = z
  .string()
  .optional()
  .catch(undefined)
  .transform((raw) => {
    if (raw === undefined) return [] as (typeof ADMIN_PAYOUT_STATUSES)[number][];
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter((s): s is (typeof ADMIN_PAYOUT_STATUSES)[number] =>
        (ADMIN_PAYOUT_STATUSES as readonly string[]).includes(s),
      );
  });

const inspectorIdSchema = z.string().optional().catch(undefined);
const dateSchema = z.string().optional().catch(undefined);

export const payoutFiltersSchema = z.object({
  status: statusSchema,
  inspectorId: inspectorIdSchema,
  dateFrom: dateSchema,
  dateTo: dateSchema,
});

export type PayoutFilters = z.infer<typeof payoutFiltersSchema>;

/** Status filter options (multi-select) — labels resolved via i18n keys. */
export const STATUS_OPTIONS: StatusOption[] = ADMIN_PAYOUT_STATUSES.map((value) => ({
  value,
  labelKey: `payouts.status.${value}`,
}));

/** True when any managed filter is active (drives the Clear-filters link). */
export const hasActiveFilters = (filters: PayoutFilters): boolean =>
  (filters.status?.length ?? 0) > 0 ||
  Boolean(filters.inspectorId && filters.inspectorId.length > 0) ||
  Boolean(filters.dateFrom && filters.dateFrom.length > 0) ||
  Boolean(filters.dateTo && filters.dateTo.length > 0);
